import { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader, UserPlus, Search } from 'lucide-react';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { useDebounce } from '../../hooks/useDebounce';
import { useStaffListSearch } from '../../hooks/useStaffListSearch';
import { usernameCacheService, useUsernameCacheVersion } from '../../services/usernameCache';
import { PlayerProfile } from '../PlayerProfile';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../utils/cn';

interface AssignStaffModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (staffUuid: string) => Promise<void>;
  title?: string;
  currentAssigned?: string | null;
}

export const AssignStaffModal = ({ isOpen, onClose, onSubmit, title = 'Assign Staff', currentAssigned }: AssignStaffModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const { identity } = useAuth();
  const [query, setQuery] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debouncedQuery = useDebounce(query, 300);
  const { matches, searching, error: staffSearchError } = useStaffListSearch({
    debouncedQuery,
    enabled: isOpen,
  });
  const usernameCacheVersion = useUsernameCacheVersion();
  const results = useMemo(
    () =>
      matches.map((s) => ({
        uuid: s.minecraft_uuid,
        name:
          usernameCacheService.getName(s.minecraft_uuid) ?? s.minecraft_uuid,
      })),
    [matches, usernameCacheVersion]
  );

  useOnClickOutside(modalRef, onClose);

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setError(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSelect = async (uuid: string) => {
    setIsSubmitting(true);
    setError(null);
    try {
      await onSubmit(uuid);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign staff');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAssignMe = () => {
    const myUuid = identity?.raw?.custom?.minecraft_uuid;
    if (myUuid) handleSelect(myUuid);
  };

  return createPortal(
    <div className="modal-overlay z-[9999]">
      <div
        ref={modalRef}
        className="modal-panel max-w-lg"
      >
        <button
          onClick={onClose}
          className="modal-close"
          title="Close"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="modal-header">
          <h2 className="modal-title flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-accent-400" />
            {title}
          </h2>
        </div>

        {currentAssigned && (
          <div className="mb-4 subcard px-3 py-2 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <span className="eyebrow">Current</span>
              <PlayerProfile uuid={currentAssigned} inline showUuid={false} />
            </div>
            <span className="text-[10px] font-mono text-glass-500 shrink-0">{currentAssigned.slice(0, 8)}...</span>
          </div>
        )}

        <div className="space-y-3">
          {identity?.raw?.custom?.minecraft_uuid && identity.raw.custom.minecraft_uuid !== currentAssigned && (
            <button
              onClick={handleAssignMe}
              disabled={isSubmitting}
              className="btn-primary w-full"
            >
              {isSubmitting ? <Loader className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
              Assign Myself
            </button>
          )}

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400" />
            <input
              className="glass-input w-full pl-9"
              placeholder="Search by username…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
              autoComplete="off"
            />
            {searching && <Loader className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-glass-400" />}
          </div>

          {query.length > 0 && query.length < 2 && (
            <p className="text-xs text-glass-500 px-1">Type at least 2 characters…</p>
          )}

          {results.length > 0 && (
            <div className="subcard overflow-hidden max-h-[240px] overflow-y-auto">
              {results.map((player) => (
                <button
                  key={player.uuid}
                  onClick={() => handleSelect(player.uuid)}
                  disabled={isSubmitting}
                  className={cn(
                    'w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-glass-200/10 transition-colors',
                    player.uuid === currentAssigned && 'bg-accent-500/10'
                  )}
                >
                  <PlayerProfile uuid={player.uuid} name={player.name} inline showUuid={false} />
                  <span className="text-[10px] text-glass-500 font-mono flex-shrink-0">{player.uuid.slice(0, 8)}...</span>
                </button>
              ))}
            </div>
          )}

          {staffSearchError && (
            <p className="text-xs text-red-400 px-1">{staffSearchError}</p>
          )}

          {debouncedQuery.length >= 2 && !searching && !staffSearchError && results.length === 0 && (
            <p className="text-xs text-glass-500 px-1">No staff found</p>
          )}

          {error && (
            <div className="alert alert-danger" role="alert">
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="glass-button-secondary" disabled={isSubmitting}>
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
